import type {
  AdvisorDraft,
  AdvisorDraftLeg,
  AdvisorPoint,
  AdvisorRegion,
} from './types';
import {
  chapterRegionOf,
  regionAtPoint,
  regionForCity,
} from './region-at-point';

// Every chapter is one of the country's top-level regions (XT custom
// regions), as an XT chapter's `regionId` is.

const nextId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

/** Where the chapter is: its base hotel, else its first item with a point. */
const chapterPoint = (leg: AdvisorDraftLeg): AdvisorPoint | null =>
  leg.baseHotel?.location ??
  leg.days.flatMap((day) => day.items).find((item) => item.location)
    ?.location ??
  null;

/**
 * Chapters of `country` not tied to one of its top-level regions — named
 * after a city, a Generate stay, a sub-region — move to the region holding
 * their hotel (else first item), or the nearest when the chapter is known to
 * be in that country. One that lands on a region the trip already has joins
 * it: its days follow that chapter's, whose base hotel is kept. With nothing
 * on the map, the one region whose XT city list names the chapter's city.
 * Chapters of other countries, and ones still unplaced, stay as they are.
 * Nothing to move returns the draft.
 */
export const assignChapterRegions = (
  draft: AdvisorDraft,
  regions: readonly AdvisorRegion[],
  country: string
): AdvisorDraft => {
  const inCountry = (leg: AdvisorDraftLeg) =>
    !leg.country ||
    leg.country.trim().toLowerCase() === country.trim().toLowerCase();
  const topLevel = new Set(
    regions.filter((region) => region.parentId === null).map((r) => r.id)
  );
  if (topLevel.size === 0) return draft;
  let changed = false;
  const legs: AdvisorDraftLeg[] = [];
  for (const leg of draft.legs) {
    let target = leg;
    if (!topLevel.has(leg.regionId) && inCountry(leg)) {
      const known = regions.find((region) => region.id === leg.regionId);
      const point = chapterPoint(leg);
      const region = known
        ? chapterRegionOf(known, regions)
        : point
          ? regionAtPoint(point, regions, null, Boolean(leg.country))
          : regionForCity(leg.regionTitle, regions);
      if (region && topLevel.has(region.id)) {
        changed = true;
        target = {
          ...leg,
          regionId: region.id,
          regionTitle: region.title,
          country: leg.country ?? region.countries[0] ?? null,
        };
      }
    }
    const same =
      target === leg
        ? -1
        : legs.findIndex((entry) => entry.regionId === target.regionId);
    const host = legs[same];
    if (host) {
      legs[same] = {
        ...host,
        baseHotel: host.baseHotel ?? target.baseHotel ?? null,
        days: [...host.days, ...target.days],
      };
      continue;
    }
    legs.push(target);
  }
  return changed ? { ...draft, legs } : draft;
};

/** A chapter for the region with one empty day; the trip's own if it has one. */
export const addRegionChapter = (
  draft: AdvisorDraft,
  region: AdvisorRegion,
  country: string | null,
  dayCount = 1
): AdvisorDraft =>
  draft.legs.some((leg) => leg.regionId === region.id)
    ? draft
    : {
        ...draft,
        legs: [
          ...draft.legs,
          {
            id: nextId('leg'),
            regionId: region.id,
            regionTitle: region.title,
            country: country ?? region.countries[0] ?? null,
            days: Array.from({ length: Math.max(1, dayCount) }, () => ({
              id: nextId('day'),
              items: [],
            })),
          },
        ],
      };
